import { array_from_query, number_from_query, str_from_query } from "@/api";
import { getClients } from "@/api/clients";
import { router } from "@/router/router";
import type { Client, ClientsFilters, ClientStat } from "@/types/clients";
import type { PaginationResponse } from "@/types/serverResponse";
import { useQuery, type QueryFunctionContext } from "@tanstack/vue-query";
import { computed, reactive, ref, watch } from "vue";

type ClientsQueryKey = ["clients", string, ClientsFilters];

const fetchPostsWithFilters = async (
  context: QueryFunctionContext<ClientsQueryKey>,
): Promise<PaginationResponse<Client>> => {
  const [, , filters] = context.queryKey;
  return getClients(filters);
};

export function useClientsQuery(campaignId?: string) {
  const filters = reactive<ClientsFilters>({
    status: array_from_query("status", String).filter(
      (s) => s === "online" || s === "offline",
    ),
    page: number_from_query("page", 1),
    size: number_from_query("size", 24),
    keyword: str_from_query("keyword"),
    startDate: str_from_query("start_date"),
    endDate: str_from_query("end_date"),
    campaign: campaignId ? campaignId : undefined,
  });

  watch(
    () => [router.currentRoute.value.query],
    () => {
      Object.assign(filters, {
        status: array_from_query("status", String).filter(
          (s) => s === "online" || s === "offline",
        ),
        page: number_from_query("page", 1),
        size: number_from_query("size", 24),
        keyword: str_from_query("keyword"),
        startDate: str_from_query("start_date"),
        endDate: str_from_query("end_date"),
      });
    },
  );

  const queryKey = ref<ClientsQueryKey>(["clients", campaignId ?? "", filters]);

  watch(
    filters,
    (newFilters, oldFilters) => {
      if (
        newFilters.campaign !== oldFilters?.campaign ||
        newFilters.keyword !== oldFilters?.keyword ||
        newFilters.status !== oldFilters?.status ||
        newFilters.size !== oldFilters?.size
      ) {
        newFilters.page = 1;
      }

      queryKey.value = ["clients", campaignId ?? "", newFilters];
    },
    { deep: true },
  );

  const queryResult = useQuery<
    PaginationResponse<Client>,
    Error,
    PaginationResponse<Client>,
    ClientsQueryKey
  >({
    queryKey: queryKey,
    queryFn: fetchPostsWithFilters,
    staleTime: 5 * 60 * 1000,
  });

  const totalPages = computed(() => {
    const totalItems = queryResult.data.value?.total_filter || 0;
    const pageSize = filters.size || 20;

    if (totalItems === 0) {
      return 0;
    }

    return Math.ceil(totalItems / pageSize);
  });

  return {
    ...queryResult,
    totalPages,
  };
}

export function useClientStatisticsQuery(campaignId?: string) {
  const queryResult = useQuery({
    queryKey: ["client_statistics", campaignId ?? ""],
    queryFn: async () => {
      const url = new URL("/api/v1/view/agent/stat", window.location.origin);
      if (campaignId) {
        url.searchParams.set("campaign", campaignId);
      }
      const response = await fetch(url.toString());
      const data = await response.json();
      return data.data as ClientStat[];
    },
    staleTime: 10 * 60 * 1000,
  });

  return queryResult;
}
